import { useState, useEffect, useCallback } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { AutoAwesome, ExpandMore, Restore, Save } from '@mui/icons-material';
import { systemConfigAPI } from '../../services/api';

interface PromptRow {
  key: string;
  config_key: string;
  value: {
    text: string;
    version?: string;
    category?: string;
    description?: string;
  };
}

interface PromptState {
  text: string;
  original: string;
  saving: boolean;
  saved: boolean;
  error: string | null;
}

export default function AIPromptsEditor() {
  const [prompts, setPrompts] = useState<PromptRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [states, setStates] = useState<Record<string, PromptState>>({});
  const [seeding, setSeeding] = useState(false);
  const [seedMsg, setSeedMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await systemConfigAPI.getAiPrompts();
      const rows: PromptRow[] = Array.isArray(res.data) ? res.data : [];
      setPrompts(rows);
      const initial: Record<string, PromptState> = {};
      for (const r of rows) {
        initial[r.key] = {
          text: r.value?.text ?? '',
          original: r.value?.text ?? '',
          saving: false,
          saved: false,
          error: null,
        };
      }
      setStates(initial);
    } catch (e: unknown) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load prompts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleChange = (key: string, text: string) => {
    setStates(prev => ({ ...prev, [key]: { ...prev[key], text, saved: false, error: null } }));
  };

  const handleSave = async (row: PromptRow) => {
    const s = states[row.key];
    if (!s) return;
    setStates(prev => ({ ...prev, [row.key]: { ...prev[row.key], saving: true, error: null } }));
    try {
      await systemConfigAPI.set(row.config_key, {
        text: s.text,
        version: row.value?.version ?? '1.0',
        category: row.value?.category ?? '',
        description: row.value?.description ?? '',
      });
      await systemConfigAPI.invalidatePromptCache(row.key);
      setStates(prev => ({
        ...prev,
        [row.key]: { ...prev[row.key], saving: false, saved: true, original: s.text },
      }));
      setTimeout(() => {
        setStates(prev => ({ ...prev, [row.key]: { ...prev[row.key], saved: false } }));
      }, 2500);
    } catch (e: unknown) {
      setStates(prev => ({
        ...prev,
        [row.key]: {
          ...prev[row.key],
          saving: false,
          error: e instanceof Error ? e.message : 'Save failed',
        },
      }));
    }
  };

  const handleReset = (key: string) => {
    setStates(prev => ({ ...prev, [key]: { ...prev[key], text: prev[key].original, error: null } }));
  };

  const handleSeed = async () => {
    setSeeding(true);
    setSeedMsg(null);
    try {
      const res = await systemConfigAPI.seedAiPrompts();
      setSeedMsg(`Inserted ${res.data?.inserted ?? 0} missing prompt(s).`);
      if ((res.data?.inserted ?? 0) > 0) await load();
    } catch (e: unknown) {
      setSeedMsg(e instanceof Error ? e.message : 'Seed failed');
    } finally {
      setSeeding(false);
    }
  };

  // Group prompts by category
  const grouped = prompts.reduce<Record<string, PromptRow[]>>((acc, r) => {
    const cat = r.value?.category || 'other';
    (acc[cat] = acc[cat] ?? []).push(r);
    return acc;
  }, {});
  const categories = Object.keys(grouped).sort();
  const totalCount = prompts.length;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 3 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">Loading prompts…</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <AutoAwesome fontSize="small" />
            AI Prompts
            <Chip label={`${totalCount} prompts · ${categories.length} categories`} size="small" sx={{ fontSize: '0.75rem' }} />
          </Typography>
          <Typography variant="body2" color="text.secondary">
            System-wide LLM prompts stored in <code>system_config</code>. Edits take effect on next API call — no restart needed.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          onClick={handleSeed}
          disabled={seeding}
          startIcon={seeding ? <CircularProgress size={14} /> : undefined}
        >
          {seeding ? 'Seeding…' : 'Seed Missing Defaults'}
        </Button>
      </Box>

      {loadError && (
        <Alert severity="error" onClose={() => setLoadError(null)} sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}
      {seedMsg && (
        <Alert severity="info" onClose={() => setSeedMsg(null)} sx={{ mb: 2 }}>
          {seedMsg}
        </Alert>
      )}

      {/* Category accordions */}
      {categories.map(cat => (
        <Accordion key={cat} disableGutters defaultExpanded={false} variant="outlined" sx={{ mb: 1, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle2" sx={{ fontFamily: 'monospace', mr: 1 }}>{cat}</Typography>
            <Chip label={grouped[cat].length} size="small" sx={{ height: 18, fontSize: '0.7rem' }} />
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {grouped[cat].map((row, idx) => {
              const s = states[row.key];
              const isDirty = s && s.text !== s.original;
              return (
                <Box
                  key={row.key}
                  sx={{
                    p: 2,
                    borderTop: idx === 0 ? 'none' : '1px solid',
                    borderColor: 'divider',
                    bgcolor: isDirty ? '#fffde7' : 'transparent',
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {/* Meta */}
                    <Box sx={{ width: 200, flexShrink: 0 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: 'monospace',
                          bgcolor: 'rgba(1,38,118,0.07)',
                          color: 'primary.main',
                          px: 0.75,
                          py: 0.25,
                          borderRadius: 0.5,
                          display: 'inline-block',
                          mb: 0.5,
                          wordBreak: 'break-all',
                        }}
                      >
                        {row.key}
                      </Typography>
                      {row.value?.description && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.4 }}>
                          {row.value.description}
                        </Typography>
                      )}
                      {row.value?.version && (
                        <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 0.5 }}>
                          v{row.value.version}
                          {isDirty && <span style={{ color: '#ed6c02', marginLeft: 6, fontWeight: 600 }}>edited</span>}
                        </Typography>
                      )}
                    </Box>

                    {/* Editor */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        maxRows={14}
                        value={s?.text ?? ''}
                        onChange={e => handleChange(row.key, e.target.value)}
                        inputProps={{ style: { fontFamily: 'Consolas, monospace', fontSize: '0.8125rem' } }}
                        size="small"
                      />
                      {s?.error && (
                        <Alert severity="error" sx={{ mt: 1, py: 0 }}>{s.error}</Alert>
                      )}
                      <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={s?.saving ? <CircularProgress size={13} color="inherit" /> : <Save />}
                          onClick={() => handleSave(row)}
                          disabled={s?.saving || !isDirty}
                        >
                          {s?.saving ? 'Saving…' : 'Save'}
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Restore />}
                          onClick={() => handleReset(row.key)}
                          disabled={!isDirty}
                        >
                          Reset
                        </Button>
                        {s?.saved && (
                          <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                            ✓ Saved
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </AccordionDetails>
        </Accordion>
      ))}

      {categories.length === 0 && !loading && (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          No prompts found. Click "Seed Missing Defaults" to populate.
        </Typography>
      )}
    </Box>
  );
}
